import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useId, useState, type InputHTMLAttributes } from "react";
import { IconButton } from "./Button.js";

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  label: string;
  helper?: string;
  error?: string;
  id?: string;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, helper, error, id: providedId, className, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? `${props.name ?? "field"}-${generatedId}`;
  const descriptionId = helper || error ? `${id}-description` : undefined;

  return (
    <div className="ui-field">
      <label className="ui-field__label" htmlFor={id}>{label}</label>
      <input
        ref={ref}
        id={id}
        className={["ui-field__control", className].filter(Boolean).join(" ")}
        aria-invalid={error ? true : undefined}
        aria-describedby={descriptionId}
        {...props}
      />
      {error ? <p className="ui-field__error" id={descriptionId}>{error}</p> : null}
      {!error && helper ? <p className="ui-field__help" id={descriptionId}>{helper}</p> : null}
    </div>
  );
});

export const PasswordField = forwardRef<HTMLInputElement, FieldProps>(function PasswordField(props, ref) {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const { label, helper, error, id: providedId, className, ...inputProps } = props;
  const id = providedId ?? `${inputProps.name ?? "password"}-${generatedId}`;
  const descriptionId = helper || error ? `${id}-description` : undefined;

  return (
    <div className="ui-field">
      <label className="ui-field__label" htmlFor={id}>{label}</label>
      <div className="ui-field__control-wrap">
        <input
          {...inputProps}
          ref={ref}
          id={id}
          type={visible ? "text" : "password"}
          className={["ui-field__control", "ui-field__control--password", className].filter(Boolean).join(" ")}
          aria-invalid={error ? true : undefined}
          aria-describedby={descriptionId}
        />
        <IconButton
          className="ui-field__reveal"
          type="button"
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          onClick={() => setVisible((value) => !value)}
        >
          {visible ? <EyeOff aria-hidden="true" size={18} /> : <Eye aria-hidden="true" size={18} />}
        </IconButton>
      </div>
      {error ? <p className="ui-field__error" id={descriptionId}>{error}</p> : null}
      {!error && helper ? <p className="ui-field__help" id={descriptionId}>{helper}</p> : null}
    </div>
  );
});
